import { supabase } from './supabase';
import { slugify } from './slug';
import type { Pipeline, Stage, Deal, DealActivity, DealStatus } from '@/components/pipeline/types';

// ─────────────────────────────────────────────
// CACHES (mirrors boardService pattern)
// ─────────────────────────────────────────────
const workspacePipelinesCache = new Map<string, Pipeline[]>();
const pipelineDetailsCache = new Map<string, Pipeline>();
const stagesCache = new Map<string, Stage[]>();
const dealsCache = new Map<string, Deal[]>();

export const pipelineService = {
  // ─── CACHE ───────────────────────────────
  clearCache() {
    workspacePipelinesCache.clear();
    pipelineDetailsCache.clear();
    stagesCache.clear();
    dealsCache.clear();
  },

  // ─── PIPELINES ───────────────────────────

  async getPipelinesByWorkspace(workspaceId: string): Promise<Pipeline[]> {
    if (workspacePipelinesCache.has(workspaceId)) {
      return workspacePipelinesCache.get(workspaceId)!;
    }

    const { data, error } = await supabase
      .from('pipelines')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const pipelines = (data || []).map(mapPipeline);
    workspacePipelinesCache.set(workspaceId, pipelines);
    return pipelines;
  },

  async getPipelineBySlug(workspaceId: string, slug: string): Promise<Pipeline | null> {
    const cacheKey = `${workspaceId}:${slug}`;
    if (pipelineDetailsCache.has(cacheKey)) {
      return pipelineDetailsCache.get(cacheKey)!;
    }

    const { data, error } = await supabase
      .from('pipelines')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const pipeline = mapPipeline(data);
    pipelineDetailsCache.set(cacheKey, pipeline);
    return pipeline;
  },

  async getPipelineById(pipelineId: string): Promise<Pipeline | null> {
    const { data, error } = await supabase
      .from('pipelines')
      .select('*')
      .eq('id', pipelineId)
      .maybeSingle();

    if (error) throw error;
    return data ? mapPipeline(data) : null;
  },

  async createPipeline(
    workspaceId: string,
    userId: string,
    title: string,
    description: string,
    background: Pipeline['background']
  ): Promise<Pipeline> {
    const slug = await this.generateUniqueSlug(workspaceId, title);

    const { data, error } = await supabase
      .from('pipelines')
      .insert({
        workspace_id: workspaceId,
        slug,
        title,
        description,
        background,
        hide_banner: true,
        default_currency: 'USD',
        created_by: userId,
      })
      .select()
      .single();

    if (error) throw error;

    workspacePipelinesCache.delete(workspaceId);

    // Create default stages: Qualified, Proposal Sent, Negotiation, Won (fixed), Lost (fixed)
    const defaultStages = [
      { title: 'Qualified', position: 1, deal_probability: 25, is_new: false, is_won: false, is_lost: false },
      { title: 'Proposal Sent', position: 2, deal_probability: 50, is_new: false, is_won: false, is_lost: false },
      { title: 'Negotiation', position: 3, deal_probability: 75, is_new: false, is_won: false, is_lost: false },
      { title: 'Won', position: 4, deal_probability: 100, is_new: false, is_won: true, is_lost: false },
      { title: 'Lost', position: 5, deal_probability: 0, is_new: false, is_won: false, is_lost: true },
    ];

    const stageResults = await Promise.all(
      defaultStages.map(stage =>
        supabase.from('pipeline_stages').insert({ pipeline_id: data.id, ...stage })
      )
    );

    const stageErrors = stageResults.filter(r => r.error);
    if (stageErrors.length > 0) {
      console.error('Error creating default stages:', stageErrors);
    }

    return mapPipeline(data);
  },

  async updatePipeline(
    pipelineId: string,
    updates: {
      title?: string;
      description?: string;
      background?: Pipeline['background'];
      hideBanner?: boolean;
      defaultCurrency?: string;
    }
  ): Promise<Pipeline> {
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.background !== undefined) dbUpdates.background = updates.background;
    if (updates.hideBanner !== undefined) dbUpdates.hide_banner = updates.hideBanner;
    if (updates.defaultCurrency !== undefined) dbUpdates.default_currency = updates.defaultCurrency;

    const { data, error } = await supabase
      .from('pipelines')
      .update({ ...dbUpdates, updated_at: new Date().toISOString() })
      .eq('id', pipelineId)
      .select()
      .single();

    if (error) throw error;

    workspacePipelinesCache.delete(data.workspace_id);
    pipelineDetailsCache.delete(`${data.workspace_id}:${data.slug}`);
    return mapPipeline(data);
  },

  async deletePipeline(pipelineId: string): Promise<void> {
    const { error } = await supabase.from('pipelines').delete().eq('id', pipelineId);
    if (error) throw error;
    workspacePipelinesCache.clear();
    pipelineDetailsCache.clear();
    stagesCache.delete(pipelineId);
    dealsCache.delete(pipelineId);
  },

  async generateUniqueSlug(workspaceId: string, title: string): Promise<string> {
    const baseSlug = slugify(title);

    const { data } = await supabase
      .from('pipelines')
      .select('slug')
      .eq('workspace_id', workspaceId)
      .eq('slug', baseSlug)
      .maybeSingle();

    if (!data) return baseSlug;

    let counter = 1;
    while (true) {
      const candidate = `${baseSlug}-${counter}`;
      const { data: existing } = await supabase
        .from('pipelines')
        .select('slug')
        .eq('workspace_id', workspaceId)
        .eq('slug', candidate)
        .maybeSingle();
      if (!existing) return candidate;
      counter++;
    }
  },

  // ─── STAGES ──────────────────────────────

  async getStagesByPipeline(pipelineId: string): Promise<Stage[]> {
    if (stagesCache.has(pipelineId)) {
      return stagesCache.get(pipelineId)!;
    }

    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('*')
      .eq('pipeline_id', pipelineId)
      .order('position', { ascending: true });

    if (error) throw error;

    const stages = (data || []).map(mapStage);
    stagesCache.set(pipelineId, stages);
    return stages;
  },

  async createStage(
    pipelineId: string,
    title: string,
    position: number,
    dealProbability = 50,
    bgColor?: string
  ): Promise<Stage> {
    // Insert before Won/Lost — shift their positions up
    const existing = await this.getStagesByPipeline(pipelineId);
    const fixed = existing.filter(s => s.isWon || s.isLost);

    // Shift fixed stages up
    await Promise.all(
      fixed.map(s =>
        supabase.from('pipeline_stages').update({ position: s.position + 1 }).eq('id', s.id)
      )
    );

    const { data, error } = await supabase
      .from('pipeline_stages')
      .insert({ pipeline_id: pipelineId, title, position, deal_probability: dealProbability, bg_color: bgColor })
      .select()
      .single();

    if (error) throw error;
    stagesCache.delete(pipelineId);
    return mapStage(data);
  },

  async updateStage(
    stageId: string,
    updates: { title?: string; bgColor?: string; dealProbability?: number }
  ): Promise<void> {
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.bgColor !== undefined) dbUpdates.bg_color = updates.bgColor;
    if (updates.dealProbability !== undefined) dbUpdates.deal_probability = updates.dealProbability;

    const { data, error } = await supabase
      .from('pipeline_stages')
      .update({ ...dbUpdates, updated_at: new Date().toISOString() })
      .eq('id', stageId)
      .select('pipeline_id')
      .single();

    if (error) throw error;
    stagesCache.delete(data.pipeline_id);
  },

  async deleteStage(stageId: string): Promise<void> {
    const { data: stage } = await supabase
      .from('pipeline_stages')
      .select('pipeline_id, is_new, is_won, is_lost')
      .eq('id', stageId)
      .single();

    if (!stage) throw new Error('Stage not found');
    if (stage.is_won || stage.is_lost) {
      throw new Error('Cannot delete protected stages (Won, Lost)');
    }

    const { error } = await supabase.from('pipeline_stages').delete().eq('id', stageId);
    if (error) throw error;
    stagesCache.delete(stage.pipeline_id);
  },

  async reorderStages(pipelineId: string, stageOrder: string[]): Promise<void> {
    await Promise.all(
      stageOrder.map((id, index) =>
        supabase
          .from('pipeline_stages')
          .update({ position: index })
          .eq('id', id)
          .eq('pipeline_id', pipelineId)
      )
    );
    stagesCache.delete(pipelineId);
  },

  // ─── leadS ───────────────────────────────

  async getDealsByPipeline(pipelineId: string): Promise<Deal[]> {
    if (dealsCache.has(pipelineId)) {
      return dealsCache.get(pipelineId)!;
    }

    const { data, error } = await supabase
      .from('deals')
      .select(`
        *,
        assigned_profile:profiles!leads_assigned_to_fkey(id, first_name, last_name, email, avatar_url)
      `)
      .eq('pipeline_id', pipelineId)
      .order('position', { ascending: true });

    if (error) throw error;
    
    const rawDeals = data || [];
    const contactIds = Array.from(new Set(rawDeals.map(d => d.contact_id).filter(Boolean)));
    
    let leadsMap: Record<string, any> = {};
    let clientsMap: Record<string, any> = {};

    if (contactIds.length > 0) {
      const [leadsRes, clientsRes] = await Promise.all([
        supabase.from('crm_leads').select('*').in('id', contactIds),
        supabase.from('workspace_members').select('*').in('user_id', contactIds)
      ]);
      
      leadsRes.data?.forEach(l => { leadsMap[l.id] = l; });
      
      if (clientsRes.data && clientsRes.data.length > 0) {
        const userIds = clientsRes.data.map(c => c.user_id);
        const { data: profilesData } = await supabase.from('profiles').select('*').in('id', userIds);
        
        clientsRes.data.forEach(c => { 
          clientsMap[c.user_id] = c; 
        });
        
        if (profilesData) {
          profilesData.forEach(p => {
            if (clientsMap[p.id]) {
              clientsMap[p.id].profiles = p;
            }
          });
        }
      }
    }

    const deals = rawDeals.map(d => mapDeal(d, leadsMap, clientsMap));
    dealsCache.set(pipelineId, deals);
    return deals;
  },

  async createDeal(
    pipelineId: string,
    workspaceId: string,
    userId: string,
    data: {
      title: string;
      description?: string;
      value?: number;
      currency?: string;
      priority?: string;
      source?: string;
      expectedCloseDate?: string;
      stageId?: string;
      contactId?: string;
      companyId?: string;
      assignedTo?: string;
      probability?: number;
      clientInfo?: Record<string, any>;
    }
  ): Promise<Deal> {
    // Get the position in the target stage
    const existingdeals = await this.getDealsByPipeline(pipelineId);
    const stageDeals = existingdeals.filter(d => d.stage_id === data.stageId);
    const position = stageDeals.length;

    let finalContactId = data.contactId || null;

    const { data: lead, error } = await supabase
      .from('deals')
      .insert({
        pipeline_id: pipelineId,
        workspace_id: workspaceId,
        stage_id: data.stageId || null,
        title: data.title,
        description: data.description || '',
        value: data.value || 0,
        currency: data.currency || 'USD',
        probability: data.probability || 0,
        priority: data.priority || 'medium',
        source: data.source || null,
        expected_close_date: data.expectedCloseDate || null,
        contact_id: finalContactId,
        company_id: data.companyId || null,
        assigned_to: data.assignedTo || null,
        client_info: {},
        created_by: userId,
        position,
        status: 'active',
      })
      .select()
      .single();

    if (error) throw error;
    dealsCache.delete(pipelineId);

    await supabase.from('deal_activities').insert({
      deal_id: lead.id,
      user_id: userId,
      type: 'created',
      content: 'deal created',
    });

    let leadsMap: Record<string, any> = {};
    let clientsMap: Record<string, any> = {};

    if (finalContactId) {
      const [leadsRes, clientsRes] = await Promise.all([
        supabase.from('crm_leads').select('*').eq('id', finalContactId).maybeSingle(),
        supabase.from('workspace_members').select('*').eq('user_id', finalContactId).maybeSingle()
      ]);
      
      if (leadsRes.data) leadsMap[leadsRes.data.id] = leadsRes.data;
      if (clientsRes.data) {
        clientsMap[clientsRes.data.user_id] = clientsRes.data;
        const { data: profileData } = await supabase.from('profiles').select('*').eq('id', finalContactId).maybeSingle();
        if (profileData) {
          clientsMap[clientsRes.data.user_id].profiles = profileData;
        }
      }
    }

    return mapDeal(lead, leadsMap, clientsMap);
  },

  async updateDeal(
    dealId: string,
    updates: Partial<{
      title: string;
      description: string;
      value: number;
      currency: string;
      probability: number;
      priority: string;
      source: string;
      expectedCloseDate: string | null;
      stageId: string | null;
      contactId: string | null;
      companyId: string | null;
      assignedTo: string | null;
      clientInfo: Record<string, any>;
    }>
  ): Promise<void> {
    const dbUpdates: any = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.value !== undefined) dbUpdates.value = updates.value;
    if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
    if (updates.probability !== undefined) dbUpdates.probability = updates.probability;
    if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
    if (updates.source !== undefined) dbUpdates.source = updates.source;
    if (updates.expectedCloseDate !== undefined) dbUpdates.expected_close_date = updates.expectedCloseDate;
    if (updates.stageId !== undefined) dbUpdates.stage_id = updates.stageId;
    if (updates.contactId !== undefined) dbUpdates.contact_id = updates.contactId;
    if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId;
    if (updates.assignedTo !== undefined) dbUpdates.assigned_to = updates.assignedTo;
    if (updates.clientInfo !== undefined) dbUpdates.client_info = updates.clientInfo;

    const { data, error } = await supabase
      .from('deals')
      .update({ ...dbUpdates, updated_at: new Date().toISOString() })
      .eq('id', dealId)
      .select('pipeline_id')
      .single();

    if (error) throw error;
    dealsCache.delete(data.pipeline_id);
  },

  async updateDealContact(
    contactId: string,
    contactType: 'lead' | 'client',
    updates: any
  ): Promise<void> {
    if (contactType === 'lead') {
      const dbUpdates: any = {};
      if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
      if (updates.email !== undefined) dbUpdates.email = updates.email;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.companyName !== undefined) dbUpdates.company_name = updates.companyName;
      if (updates.companyCategory !== undefined) dbUpdates.company_category = updates.companyCategory;
      if (updates.designation !== undefined) dbUpdates.designation = updates.designation;
      if (updates.source !== undefined) dbUpdates.source = updates.source;
      if (updates.website !== undefined) dbUpdates.website = updates.website;
      if (updates.address !== undefined) dbUpdates.address = updates.address;
      if (updates.socialMedia !== undefined) dbUpdates.social_media = updates.socialMedia;
      if (updates.emailType !== undefined) dbUpdates.email_type = updates.emailType;
      if (updates.phoneType !== undefined) dbUpdates.phone_type = updates.phoneType;
      if (updates.additionalEmails !== undefined) dbUpdates.additional_emails = updates.additionalEmails;
      if (updates.additionalPhones !== undefined) dbUpdates.additional_phones = updates.additionalPhones;

      if (Object.keys(dbUpdates).length > 0) {
        const { error } = await supabase
          .from('crm_leads')
          .update({ ...dbUpdates, updated_at: new Date().toISOString() })
          .eq('id', contactId);
        if (error) throw error;
      }
    } else if (contactType === 'client') {
      // For clients, we update profiles (first/last name) and workspace_members (everything else)
      const profileUpdates: any = {};
      if (updates.firstName !== undefined) profileUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) profileUpdates.last_name = updates.lastName;
      if (updates.designation !== undefined) profileUpdates.designation = updates.designation;
      if (updates.source !== undefined) profileUpdates.source = updates.source;
      if (updates.phone !== undefined) profileUpdates.phone = updates.phone;
      if (updates.emailType !== undefined) profileUpdates.email_type = updates.emailType;
      if (updates.phoneType !== undefined) profileUpdates.phone_type = updates.phoneType;
      if (updates.socialMedia !== undefined) profileUpdates.social_media = updates.socialMedia;
      if (updates.additionalEmails !== undefined) profileUpdates.additional_emails = updates.additionalEmails;
      if (updates.additionalPhones !== undefined) profileUpdates.additional_phones = updates.additionalPhones;
      // We explicitly skip updating profiles.email per user instructions.

      if (Object.keys(profileUpdates).length > 0) {
        const { error } = await supabase
          .from('profiles')
          .update({ ...profileUpdates, updated_at: new Date().toISOString() })
          .eq('id', contactId);
        if (error) throw error;
      }

      const memberUpdates: any = {};
      if (updates.firstName !== undefined) memberUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) memberUpdates.last_name = updates.lastName;
      if (updates.companyName !== undefined) memberUpdates.company_name = updates.companyName;
      if (updates.companyCategory !== undefined) memberUpdates.category = updates.companyCategory;
      if (updates.website !== undefined) memberUpdates.website = updates.website;
      if (updates.address !== undefined) {
        memberUpdates.country = updates.address.country;
        memberUpdates.state = updates.address.state;
        memberUpdates.city = updates.address.city;
        memberUpdates.zip = updates.address.zip;
        memberUpdates.street_address = updates.address.streetAddress;
      }
      
      if (Object.keys(memberUpdates).length > 0) {
        const { error } = await supabase
          .from('workspace_members')
          .update(memberUpdates)
          .eq('user_id', contactId);
        if (error) throw error;
      }
    }
    
    // Clear cache because the deal's dynamic info has changed
    dealsCache.clear();
  },

  async moveDeal(dealId: string, toStageId: string, position: number): Promise<void> {
    const { data, error } = await supabase
      .from('deals')
      .update({
        stage_id: toStageId,
        position,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealId)
      .select('pipeline_id')
      .single();

    if (error) throw error;

    // Update positions of other deals in the target stage
    const { data: stageDeals } = await supabase
      .from('deals')
      .select('id')
      .eq('stage_id', toStageId)
      .eq('status', 'active')
      .neq('id', dealId)
      .order('position', { ascending: true });

    if (stageDeals) {
      await Promise.all(
        stageDeals.map((d, idx) =>
          supabase
            .from('deals')
            .update({ position: idx >= position ? idx + 1 : idx })
            .eq('id', d.id)
        )
      );
    }

    dealsCache.delete(data.pipeline_id);
  },

  async markDealWon(
    dealId: string,
    userId: string,
    reason?: string
  ): Promise<void> {
    // Get won stage
    const { data: lead } = await supabase
      .from('deals')
      .select('pipeline_id')
      .eq('id', dealId)
      .single();

    if (!lead) throw new Error('lead not found');

    const { data: wonStage } = await supabase
      .from('pipeline_stages')
      .select('id')
      .eq('pipeline_id', lead.pipeline_id)
      .eq('is_won', true)
      .single();

    const { error } = await supabase
      .from('deals')
      .update({
        status: 'won' as DealStatus,
        stage_id: wonStage?.id || null,
        won_reason: reason || null,
        actual_close_date: new Date().toISOString().split('T')[0],
        probability: 100,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealId);

    if (error) throw error;

    await supabase.from('deal_activities').insert({
      deal_id: dealId,
      user_id: userId,
      type: 'status_change',
      content: reason ? `lead marked as won. Reason: ${reason}` : 'lead marked as won',
      metadata: { status: 'won', reason },
    });

    dealsCache.delete(lead.pipeline_id);
  },

  async markDealLost(
    dealId: string,
    userId: string,
    reason: string
  ): Promise<void> {
    const { data: lead } = await supabase
      .from('deals')
      .select('pipeline_id')
      .eq('id', dealId)
      .single();

    if (!lead) throw new Error('lead not found');

    const { data: lostStage } = await supabase
      .from('pipeline_stages')
      .select('id')
      .eq('pipeline_id', lead.pipeline_id)
      .eq('is_lost', true)
      .single();

    const { error } = await supabase
      .from('deals')
      .update({
        status: 'lost' as DealStatus,
        stage_id: lostStage?.id || null,
        lost_reason: reason,
        actual_close_date: new Date().toISOString().split('T')[0],
        probability: 0,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealId);

    if (error) throw error;

    await supabase.from('deal_activities').insert({
      deal_id: dealId,
      user_id: userId,
      type: 'status_change',
      content: `lead marked as lost. Reason: ${reason}`,
      metadata: { status: 'lost', reason },
    });

    dealsCache.delete(lead.pipeline_id);
  },

  async reopenDeal(dealId: string, userId: string, stageId: string): Promise<void> {
    const { data: lead, error } = await supabase
      .from('deals')
      .update({
        status: 'active' as DealStatus,
        stage_id: stageId,
        actual_close_date: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealId)
      .select('pipeline_id')
      .single();

    if (error) throw error;

    await supabase.from('deal_activities').insert({
      deal_id: dealId,
      user_id: userId,
      type: 'status_change',
      content: 'lead reopened',
      metadata: { status: 'active' },
    });

    dealsCache.delete(lead.pipeline_id);
  },

  async deleteDeal(dealId: string): Promise<void> {
    const { data } = await supabase
      .from('deals')
      .select('pipeline_id')
      .eq('id', dealId)
      .single();

    const { error } = await supabase.from('deals').delete().eq('id', dealId);
    if (error) throw error;
    if (data) dealsCache.delete(data.pipeline_id);
  },

  // ─── ACTIVITIES ──────────────────────────

  async getActivities(dealId: string): Promise<DealActivity[]> {
    const { data, error } = await supabase
      .from('deal_activities')
      .select(`
        *,
        user:profiles(first_name, last_name, email, avatar_url)
      `)
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapActivity);
  },

  async addActivity(
    dealId: string,
    userId: string,
    type: DealActivity['type'],
    content: string,
    metadata: Record<string, any> = {}
  ): Promise<DealActivity> {
    const { data, error } = await supabase
      .from('deal_activities')
      .insert({ deal_id: dealId, user_id: userId, type, content, metadata })
      .select()
      .single();

    if (error) throw error;
    return mapActivity(data);
  },

  async updateActivity(
    activityId: string,
    content: string
  ): Promise<DealActivity> {
    const { data, error } = await supabase
      .from('deal_activities')
      .update({ content })
      .eq('id', activityId)
      .select(`
        *,
        user:profiles(first_name, last_name, email, avatar_url)
      `)
      .single();

    if (error) throw error;
    return mapActivity(data);
  },

  async deleteActivity(activityId: string): Promise<void> {
    const { error } = await supabase
      .from('deal_activities')
      .delete()
      .eq('id', activityId);
    if (error) throw error;
  },
};

// ─── MAPPERS ────────────────────────────────

function mapPipeline(row: any): Pipeline {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    slug: row.slug,
    title: row.title,
    description: row.description || '',
    background: row.background || { type: 'gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    hideBanner: row.hide_banner ?? true,
    defaultCurrency: row.default_currency || 'USD',
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapStage(row: any): Stage {
  return {
    id: row.id,
    pipeline_id: row.pipeline_id,
    title: row.title,
    position: row.position,
    bgColor: row.bg_color || undefined,
    dealProbability: row.deal_probability ?? 0,
    isNew: row.is_new ?? false,
    isWon: row.is_won ?? false,
    isLost: row.is_lost ?? false,
  };
}

function mapDeal(row: any, leadsMap?: Record<string, any>, clientsMap?: Record<string, any>): Deal {
  const ap = row.assigned_profile;
  
  let dynamicClientInfo = row.client_info || {};
  let contactType: 'lead' | 'client' | undefined = undefined;

  if (row.contact_id) {
    if (leadsMap && leadsMap[row.contact_id]) {
      const l = leadsMap[row.contact_id];
      contactType = 'lead';
      dynamicClientInfo = {
        ...dynamicClientInfo,
        firstName: l.first_name || dynamicClientInfo.firstName,
        lastName: l.last_name || dynamicClientInfo.lastName,
        email: l.email || dynamicClientInfo.email,
        phone: l.phone || dynamicClientInfo.phone,
        companyName: l.company_name || dynamicClientInfo.companyName,
        companyCategory: l.company_category || dynamicClientInfo.companyCategory,
        designation: l.designation || dynamicClientInfo.designation,
        source: l.source || dynamicClientInfo.source,
        website: l.website || dynamicClientInfo.website,
        address: l.address || dynamicClientInfo.address,
        socialMedia: l.social_media || dynamicClientInfo.socialMedia,
        emailType: l.email_type || dynamicClientInfo.emailType,
        phoneType: l.phone_type || dynamicClientInfo.phoneType,
        additionalEmails: l.additional_emails || dynamicClientInfo.additionalEmails,
        additionalPhones: l.additional_phones || dynamicClientInfo.additionalPhones,
      };
    } else if (clientsMap && clientsMap[row.contact_id]) {
      const c = clientsMap[row.contact_id];
      const p = c.profiles || {};
      contactType = 'client';
      dynamicClientInfo = {
        ...dynamicClientInfo,
        firstName: c.first_name || p.first_name || dynamicClientInfo.firstName,
        lastName: c.last_name || p.last_name || dynamicClientInfo.lastName,
        email: p.email || dynamicClientInfo.email,
        phone: p.phone || dynamicClientInfo.phone,
        companyName: c.company_name || dynamicClientInfo.companyName,
        companyCategory: c.category || dynamicClientInfo.companyCategory,
        designation: p.designation || c.designation || dynamicClientInfo.designation,
        source: p.source || c.source || dynamicClientInfo.source,
        website: c.website || dynamicClientInfo.website,
        address: {
          country: c.country,
          state: c.state,
          city: c.city,
          zip: c.zip,
          streetAddress: c.street_address
        },
        // We'll trust whatever social media was already on the deal for clients if not in member data
      };
    }
  }

  return {
    id: row.id,
    pipeline_id: row.pipeline_id,
    workspace_id: row.workspace_id,
    stage_id: row.stage_id,
    title: row.title,
    description: row.description || '',
    value: Number(row.value) || 0,
    currency: row.currency || 'USD',
    probability: row.probability || 0,
    status: row.status || 'active',
    priority: row.priority || 'medium',
    source: row.source || undefined,
    expectedCloseDate: row.expected_close_date || undefined,
    actualCloseDate: row.actual_close_date || undefined,
    wonReason: row.won_reason || undefined,
    lostReason: row.lost_reason || undefined,
    position: row.position || 0,
    contactId: row.contact_id || undefined,
    companyId: row.company_id || undefined,
    assignedTo: row.assigned_to || undefined,
    createdBy: row.created_by || undefined,
    clientInfo: { ...dynamicClientInfo, contactType },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    assignee: ap
      ? {
          id: ap.id,
          name: `${ap.first_name || ''} ${ap.last_name || ''}`.trim() || ap.email,
          email: ap.email,
          avatar: ap.avatar_url,
        }
      : undefined,
    contact: (dynamicClientInfo.firstName || dynamicClientInfo.lastName || dynamicClientInfo.email)
      ? {
          id: row.contact_id,
          name: `${dynamicClientInfo.firstName || ''} ${dynamicClientInfo.lastName || ''}`.trim() || dynamicClientInfo.email,
          email: dynamicClientInfo.email,
        }
      : undefined,
    company: dynamicClientInfo.companyName
      ? {
          id: row.company_id,
          name: dynamicClientInfo.companyName,
        }
      : undefined,
  };
}

function mapActivity(row: any): DealActivity {
  const u = row.user;
  return {
    id: row.id,
    deal_id: row.deal_id,
    user_id: row.user_id,
    type: row.type,
    content: row.content,
    metadata: row.metadata || {},
    created_at: row.created_at,
    user: u
      ? {
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email,
          avatar: u.avatar_url,
        }
      : undefined,
  };
}
